// The link a tapped widget opens.
//
// A widget is a button that has to work from a cold start: the app is not
// running, the timeline entry was drawn minutes ago, and the only thing that
// crosses from the home screen into the app is a URL. So the widget puts the
// station it is showing into that URL and the app opens that board, rather than
// opening on whatever the default happened to be — otherwise tapping a widget
// for a station you are standing at can land you at the other end of your
// commute.
//
// TWO SPELLINGS OF ONE LINK, because the two platforms deliver it differently
// and neither is optional:
//
//   lateagain://station/1034?city=melbourne
//     The custom scheme. A widget's own deep link never leaves the device, so
//     it needs no domain, no association file and no verification — it works on
//     the first launch after install, which https links do not always do.
//
//   https://lateagain.harfredlabs.com/open?stop=1034&city=melbourne
//     The universal/app link twin, already claimed by `web.deepLinks` in
//     app.config.json and already served in the association files. It exists so
//     the same URL can be sent to somebody who does not have the app: the web
//     app reads the same query and opens the same board.
//
// `share-links` had not landed on main when this was written, so this is a
// scheme of its own rather than a reuse of one. It is deliberately a SUBSET of
// what a share link would need — one station, one city, no run ref, no plan —
// so a later share-link parser can absorb it without either side changing.
//
// PURE, and its one import is the generated constants file, so the test can run
// it and `src/main.js` can call it during boot before anything is hydrated.

import { PUBLIC_ORIGIN } from '../app-config.js';

/**
 * Hosts whose https links this app still opens, besides `PUBLIC_ORIGIN`.
 *
 * The app moved from lateagain.jeremymarks.com.au to lateagain.harfredlabs.com.
 * Both names serve the same service and both are claimed as universal/app
 * links, so a link written by an older build (or already sitting in somebody's
 * messages) must keep opening the right board. INCOMING links accept both;
 * everything the app WRITES uses `PUBLIC_ORIGIN` only.
 */
export const LEGACY_ORIGINS = ['https://lateagain.jeremymarks.com.au'];

/** The custom scheme the iOS and Android widgets both use. */
export const WIDGET_SCHEME = 'lateagain';

/** The https path the same link takes. Must match `web.deepLinks`. */
export const WIDGET_PATH = '/open';

/**
 * The station a deep link asks for: `{ stop, city }`, or null.
 *
 * `city` may be null, and is a HINT rather than a command — a stop id means
 * nothing without one (Sydney's and Melbourne's are disjoint today, and relying
 * on that is relying on two transport agencies never colliding), but the app
 * validates it against the city list and resolves the stop against the bundle
 * in force. Anything unparseable, unrecognised, or carrying a stop id that is
 * not a positive integer comes back null and the app opens where it always
 * does: a widget tapped after a reinstall should open the app, not throw on its
 * first frame.
 */
export function parseWidgetLink(url) {
  const parsed = parse(url);
  // A TRAIN LINK NEEDS ONLY ITS RUN. The stop is where somebody boards and is
  // worth having, but a card tapped for a train whose boarding stop the bundle
  // cannot place must still open that train.
  if (parsed?.runRef) return parsed;
  if (!parsed?.stop) return null;
  return parsed;
}

/**
 * The link a widget puts behind itself, in the custom scheme.
 *
 * Here rather than only in Swift and Kotlin so the one test that matters — the
 * app can open what the widget writes — can be written in one language.
 */
export function widgetLink(stopId, city) {
  const stop = positive(stopId);
  if (!stop) return null;
  const id = cityOf(city);
  return `${WIDGET_SCHEME}://station/${stop}${id ? `?city=${id}` : ''}`;
}

/** The stop and city in a link, in whichever of the two spellings it arrived. */
function parse(url) {
  const text = String(url ?? '').trim();
  if (!text) return null;

  let u;
  try { u = new URL(text); } catch { return null; }

  if (u.protocol === `${WIDGET_SCHEME}:`) {
    // `lateagain://train/967508/1034?route=11`. THE CARD IS ABOUT ONE TRAIN and
    // tapping it has to open that train — see `trainLink` below for what was
    // happening instead.
    if (u.hostname === 'train') {
      // ONE LEADING SLASH, not all of them. `lateagain://train//1034` is a
      // malformed link with no ref in it, and stripping every slash turned it
      // into a request for run 1034 — somebody else's train, opened
      // confidently. Dropping exactly the path separator leaves the empty
      // segment empty, which is what the guard below is for.
      const [ref, stop] = u.pathname.replace(/^\//, '').split('/');
      const runRef = String(ref ?? '').trim();
      if (!runRef) return null;
      return {
        runRef,
        stop: positive(stop),
        routeId: positive(u.searchParams.get('route')),
        city: cityOf(u.searchParams.get('city')),
      };
    }
    // `lateagain://run/tram/5521/1034?route=6019`: a ride on one of the
    // registry's modes. The mode id is a path segment, so a new mode needs no
    // new host; older builds wrote `train` for it, which the app redirects.
    if (u.hostname === 'run') {
      const [mode, ref, stop] = u.pathname.replace(/^\//, '').split('/');
      const runRef = String(ref ?? '').trim();
      const word = String(mode ?? '').trim().toLowerCase();
      if (!runRef || !/^[a-z-]{2,24}$/.test(word)) return null;
      return {
        runRef,
        mode: word,
        stop: positive(stop),
        routeId: positive(u.searchParams.get('route')),
        city: cityOf(u.searchParams.get('city')),
      };
    }
    // `lateagain://station/1034`. The host is "station" and the id is the
    // first path segment — URL parses a custom scheme's authority the same way
    // it parses http's, so `u.hostname` is the word and `u.pathname` the rest.
    if (u.hostname !== 'station') return null;
    return {
      stop: positive(u.pathname.replace(/^\/+/, '').split('/')[0]),
      city: cityOf(u.searchParams.get('city')),
    };
  }

  // THE ORIGIN IS CHECKED, not just the path. `https://evil.example/open?stop=…`
  // parses exactly as ours does, and a handler that acted on it would let any
  // page on the internet drive the app's navigation with one tapped link.
  if (u.protocol === 'https:'
    && (u.origin === PUBLIC_ORIGIN || LEGACY_ORIGINS.includes(u.origin))
    && u.pathname.replace(/\/+$/, '') === WIDGET_PATH) {
    return {
      stop: positive(u.searchParams.get('stop')),
      city: cityOf(u.searchParams.get('city')),
    };
  }

  return null;
}

/**
 * The link a LIVE ACTIVITY puts behind itself: one train, not one station.
 *
 * THE CARD USED TO LINK TO A STATION. `lateagain://station/1034` is what the
 * activity handed the system, the app parsed it exactly as written, and
 * somebody tapping a card about the 17:47 landed on the Trains tab showing the
 * board at Carnegie. Nothing was broken in the handling — the link simply
 * never asked for the train it was about.
 *
 * THE RUN REF IS THE WHOLE POINT and the stop is what makes the card's own
 * page readable: the train page needs to know where this person boards to know
 * which half of the pattern is behind them. The route id rides along because a
 * ref is not unique across route types, and reading a metropolitan ref against
 * V/Line's table opens the wrong train.
 */
export function trainLink(runRef, stopId, { routeId = 0, city = null } = {}) {
  const ref = String(runRef ?? '').trim();
  if (!ref) return null;
  const query = [
    positive(routeId) ? `route=${positive(routeId)}` : '',
    cityOf(city) ? `city=${cityOf(city)}` : '',
  ].filter(Boolean).join('&');
  return `${WIDGET_SCHEME}://train/${encodeURIComponent(ref)}/${positive(stopId)}${query ? `?${query}` : ''}`;
}

/**
 * The link for a ride on a mode: `lateagain://run/<mode>/<ref>/<stop>`. A train
 * keeps `trainLink`. Mirrored by `FollowStyle.link` (Swift) and `openTrip` (Kotlin).
 */
export function runLink(modeId, runRef, stopId, { routeId = 0, city = null } = {}) {
  const ref = String(runRef ?? '').trim();
  const mode = String(modeId ?? '').trim().toLowerCase();
  if (!ref || !mode) return null;
  const query = [
    positive(routeId) ? `route=${positive(routeId)}` : '',
    cityOf(city) ? `city=${cityOf(city)}` : '',
  ].filter(Boolean).join('&');
  return `${WIDGET_SCHEME}://run/${mode}/${encodeURIComponent(ref)}/${positive(stopId)}${query ? `?${query}` : ''}`;
}

/** A stop id, or 0 for anything that is not one. */
function positive(value) {
  const n = Number(value);
  return Number.isInteger(n) && n > 0 ? n : 0;
}

/** A city id shaped like one. Validated against the real list by the reader. */
function cityOf(value) {
  const id = String(value ?? '').trim().toLowerCase();
  return /^[a-z-]{3,24}$/.test(id) ? id : null;
}
